// src/pages/league-dashboard/free-agent-search/TableColumns.js - CANONICAL 2-ROW FORMAT WITH MY ROSTER STYLING
import React from 'react';
import { UserCheck, RefreshCw, Lock, UserPlus, TrendingUp } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { formatContractDisplay } from '../../../services/contractUtils';
import { useBatchSelection } from './BatchSelectionProvider';
import PlayerLink from '../../../components/PlayerLink';
import InjuryBadge from '../../../components/InjuryBadge';
// 9o.6.e Commit 8: window-aware stats helpers. Rolling row now sources from
// the active dateRange's rolling slot (was hardcoded to rolling_14_day);
// client-side sortValue aligns with server-side ORDER BY (Commit 1).
import {
  getRollingStatsForWindow,
  getSortStatsForWindow,
  getRollingRowLabel,
} from '../../../utils/playerStatsWindow';

// Render helper functions
export const renderHelpers = {
  renderDefault: (v) => v ?? 0,
  renderFloat1: (v) => (parseFloat(v) || 0).toFixed(1),
  renderFloat2: (v) => (parseFloat(v) || 0).toFixed(2),
  renderFloat3: (v) => formatBattingAvg(v),
  renderPercent: (v) => `${(parseFloat(v) || 0).toFixed(1)}%`,
  renderAvg: (v) => formatBattingAvg(v)
};

// Checkbox Column Component
const CheckboxColumn = ({ player, disabled = false }) => {
  const { isPlayerSelected, togglePlayerSelection } = useBatchSelection();
  
  const leaguePlayerId = player.ids?.league_player;
  const isSelected = isPlayerSelected(player);
  const isDisabled = disabled || !leaguePlayerId || !!player.league_context?.team?.team_name;
  
  return (
    <div className="flex justify-center">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => !isDisabled && togglePlayerSelection(player)}
        disabled={isDisabled}
        className={`w-4 h-4 rounded border-2 ${
          isDisabled
            ? 'border-gray-600 bg-gray-700 cursor-not-allowed opacity-50'
            : isSelected
            ? 'border-yellow-400 bg-yellow-400 text-black'
            : 'border-gray-400 bg-transparent hover:border-yellow-400'
        } focus:ring-2 focus:ring-yellow-400/50 transition-colors`}
      />
    </div>
  );
};

// Action Button Component
// CHANGE B: if player is on waivers, label = "Claim" and action = waiver claim flow.
// Bible § 4.5.19 (2026-05-14): the cutting team sees "Cooling Off" (disabled) during
// the active waiver period; every other team sees "Claim" (enabled). After
// waiver_expires_at passes, everyone (including the cutter) sees Claim/Add enabled.
const ActionButton = ({ player, handleAddPlayer, transactionsEnabled, addingPlayer, loading, isCommissionerMode, activeTeamName, myTeamId, leagueStatus, leagueId, currentRoster }) => {
  const teamName = player.league_context?.team?.team_name;

  if (teamName) {
    return (
      <div className="flex items-center gap-1 text-xs">
        <UserCheck className="w-3 h-3 text-green-400" />
        <span className={dynastyTheme.classes.text.success}>Owned</span>
      </div>
    );
  }

  const leaguePlayerId = player.ids?.league_player;
  const isDraftMode = leagueStatus === 'draft' || leagueStatus === 'offline_draft';
  const canAddPlayer = isCommissionerMode || transactionsEnabled || isDraftMode;

  // CHANGE B: detect waiver status from canonical availability namespace
  const onWaivers =
    player.league_context?.availability?.on_waivers === true ||
    player.league_context?.availability?.status === 'waivers';
  const waiverExpiresAt = player.league_context?.availability?.waiver_expires_at;
  const droppedByTeamId = player.league_context?.availability?.dropped_by_team_id;
  const isExpired = onWaivers && waiverExpiresAt
    ? new Date(waiverExpiresAt).getTime() <= Date.now()
    : false;
  const droppedByMyTeam = onWaivers && droppedByTeamId
    && myTeamId && droppedByTeamId === myTeamId;
  // Cooling-off only when the viewer cut the player AND the waiver period
  // hasn't expired yet (Bible § 4.5.19). Commissioner mode bypasses
  // because the commissioner is acting on behalf of activeTeamId, and the
  // cooling-off check applies to that team.
  const coolingOff = onWaivers && !isExpired && droppedByMyTeam;

  const buttonText = coolingOff
    ? 'Cooling Off'
    : onWaivers
      ? isCommissionerMode ? `Claim for ${activeTeamName}` : 'Claim'
      : isCommissionerMode ? `Add to ${activeTeamName}` : '+ Add';

  const playerData = JSON.stringify({
    player,
    leaguePlayerId,
    canAddPlayer,
    loading,
    onWaivers,
    leagueId,
  });

  const isLoading = addingPlayer === leaguePlayerId;
  const isDisabled = !canAddPlayer || isLoading || loading || !leaguePlayerId || coolingOff;

  return (
    <div className="flex justify-center items-center h-full">
      <button
        type="button"
        data-action={onWaivers ? 'claim-player' : 'add-player'}
        data-player={playerData}
        data-player-id={leaguePlayerId}
        disabled={isDisabled}
        className={onWaivers ? 'claim-player-btn' : 'add-player-btn'}
        style={canAddPlayer
          ? { fontSize: 11, padding: '2px 8px', borderRadius: 12, border: '1px solid rgba(250,204,21,0.5)', background: 'transparent', color: '#eab308', cursor: 'pointer', transition: 'background 0.15s', display: 'flex', alignItems: 'center', gap: 2 }
          : { fontSize: 11, padding: '2px 8px', borderRadius: 12, border: '1px solid rgba(107,114,128,0.5)', background: 'transparent', color: '#6b7280', cursor: 'not-allowed', opacity: 0.5, display: 'flex', alignItems: 'center', gap: 2 }
        }
        title={
          !canAddPlayer
            ? 'Transactions locked - prices must be set first'
            : !leaguePlayerId
            ? 'Player not available'
            : coolingOff
            ? 'Cooling off — you cut this player; wait for waivers to clear'
            : onWaivers
            ? 'Submit waiver claim'
            : isCommissionerMode
            ? `Add to ${activeTeamName}`
            : 'Add to your team'
        }
      >
        {isLoading ? (
          <RefreshCw className="w-3 h-3 animate-spin" />
        ) : !canAddPlayer ? (
          <><Lock className="w-3 h-3" /> Locked</>
        ) : (
          canAddPlayer ? buttonText : 'Locked'
        )}
      </button>
    </div>
  );
};

// REMOVED: Transform function - now passing raw canonical players to DynastyTable
// DynastyTable handles row generation internally based on rowsPerItem and rowTypes props

// Main function to create dynamic columns with 2-row format + MY ROSTER STYLING
export const createDynamicColumns = ({
  showAll,
  activeTab,
  handlePlayerClick,
  handleAddPlayer,
  transactionsEnabled,
  addingPlayer,
  loading,
  isCommissionerMode,
  activeTeamName,
  activeTeamId = null,
  bulkMode = false,
  savedPrices = {},
  leagueStatus = '',
  statsYear = 'current',
  currentSeason,
  leagueId = null,
  currentRoster = [],
  // 9o.6.e Commit 8: stats window for rolling row + sortValue. Default 'season'
  // preserves the pre-9o.6.e behavior for any caller that hasn't been wired yet.
  dateRange = 'season'
}) => {
  const columns = [];
  const { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg, renderPercent } = renderHelpers;
  const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
  const getContract = (player) => isOffseason && player.projected_contract
      ? player.projected_contract
      : player.league_context?.financial;

  // Checkbox column for selection - ONLY SHOW WHEN BULK MODE IS ENABLED
  if (bulkMode) {
    columns.push({
      key: 'select',
      title: (
        <div className="flex justify-center">
          <input
            type="checkbox"
            disabled={true}
            className="w-4 h-4 rounded border-2 border-gray-600 bg-gray-700 opacity-50"
          />
        </div>
      ),
      width: 25,
      cellPadding: 'py-0 px-1',
      sortable: false,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      render: (player, rowType) => {
        if (rowType === 'rolling') return null;
        return <CheckboxColumn player={player} disabled={loading} />;
      }
    });
  }
  
  // Position column - CANONICAL: info.position (show on season row only)
  columns.push({
    key: 'info.position',
    title: 'Pos',
    width: 36,
    cellPadding: 'py-0 px-1',
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;
      return <span className="text-xs text-neutral-400 font-medium">{player.info?.position || '--'}</span>;
    }
  });

  // Player name column - CANONICAL: info.first_name, info.last_name
  columns.push({
    key: 'info.first_name',
    title: 'Name',
    width: 110,
    cellPadding: 'py-0 pl-1 pr-0',
    textAlign: 'text-left',
    sortable: true,
    allowOverflow: true,
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    render: (player, rowType) => {
      // Season row - show player name
      if (rowType === 'season') {
        const contractType = player.league_context?.financial?.contract_type || '';
        const isExpiringOP = contractType === 'OP';
        const isOnWaivers =
          player.league_context?.availability?.on_waivers === true ||
          player.league_context?.availability?.status === 'waivers';

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', minWidth: 0 }}>
            <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim() || 'Unknown'} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }} />
            {isExpiringOP && (
              <span style={{ flexShrink: 0, fontSize: 9, color: '#f87171', border: '1px solid rgba(248,113,113,0.5)', borderRadius: 3, padding: '0 3px', fontStyle: 'normal', whiteSpace: 'nowrap' }}>
                EXPIRING
              </span>
            )}
            {isOnWaivers && (() => {
              const waiverLabel = player.league_context?.availability?.waiver_label || 'W';
              const expiresRaw = player.league_context?.availability?.waiver_expires_at;
              let expiresStr = '';
              if (expiresRaw) {
                try {
                  const d = new Date(expiresRaw);
                  expiresStr = ` ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                } catch { /* ignore */ }
              }
              return (
                <span style={{ flexShrink: 0, fontSize: 9, color: '#fb923c', border: '1px solid rgba(251,146,60,0.5)', borderRadius: 3, padding: '0 3px', fontStyle: 'normal', whiteSpace: 'nowrap' }}>
                  {waiverLabel}{expiresStr}
                </span>
              );
            })()}
            <InjuryBadge mlbId={player.ids?.mlb} />
          </div>
        );
      } else {
        // Rolling row - show stat type label (MATCH MY ROSTER EXACTLY) - REDUCED BRIGHTNESS
        // 9o.6.e Commit 8: label reflects active dateRange (was hardcoded "14-Day").
        // When dateRange='season', rolling row is hidden upstream so label is never reached.
        const rollingLabel = getRollingRowLabel(dateRange) || 'Last 14';
        return (
          <div className="flex items-center gap-1 text-left text-[10px] font-normal opacity-50 text-yellow-400">
            <TrendingUp className="w-2.5 h-2.5 text-sky-400 flex-shrink-0" style={{fontStyle:'normal'}} />
            {rollingLabel}
          </div>
        );
      }
    }
  });

  // Team column - CANONICAL: info.mlb_team (show on season row only)
  columns.push({
    key: 'info.mlb_team',
    title: 'MLB Team',
    width: 42,
    cellPadding: 'py-0 px-1',
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;
      return <span className={`${dynastyTheme.classes.text.white} text-xs`}>{player.info?.mlb_team || 'FA'}</span>;
    }
  });

  // Fantasy Team column (only in All Players mode) - CANONICAL: league_context.team.team_name
  if (showAll) {
    columns.push({
      key: 'league_context.team.team_name',
      title: 'Fantasy Team',
      width: 70,
      cellPadding: 'py-0 px-1',
      sortable: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      render: (player, rowType) => {
        if (rowType === 'rolling') return null;
        const teamName = player.league_context?.team?.team_name;
        if (teamName) {
          return (
            <span className={dynastyTheme.components.badge.success}>
              {teamName}
            </span>
          );
        }
        return (
          <span className={dynastyTheme.components.badge.neutral}>
            Free Agent
          </span>
        );
      }
    });
  }

  // Games played - CANONICAL: stats.season.hitting.games_played (or .pitching) / stats.rolling_14_day.hitting.games_played
  columns.push({
    key: 'stats.season.games_played',
    title: 'G',
    width: 30,
    cellPadding: 'py-0 px-1',
    sortable: true,
    isStatColumn: true,
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    sortValue: (player) => {
      // 9o.6.e Commit 8: sort by active window (aligns with server ORDER BY).
      const windowStats = getSortStatsForWindow(player, dateRange);
      const statGroup = activeTab === 'hitting' ? windowStats?.hitting : windowStats?.pitching;
      return statGroup?.games_played ?? 0;
    },
    render: (player, rowType) => {
      const statsData = rowType === 'season'
        ? player.stats?.season
        : getRollingStatsForWindow(player, dateRange);
      // Access nested structure: hitting or pitching based on activeTab
      const statGroup = activeTab === 'hitting' ? statsData?.hitting : statsData?.pitching;
      const value = statGroup?.games_played ?? 0;

      const colorClass = rowType === 'season'
        ? dynastyTheme.classes.text.white
        : 'text-yellow-400 opacity-60';
      const fontClass = rowType === 'season' ? '' : 'text-xs italic';

      return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
    }
  });

  if (activeTab === 'hitting') {
    // Hitter stats - ALL CANONICAL: stats.season.hitting.* / stats.rolling_14_day.hitting.*
    const hitterStats = [
      { key: 'at_bats', title: 'AB', width: 32 },
      { key: 'runs', title: 'R', width: 28 },
      { key: 'hits', title: 'H', width: 28 },
      { key: 'doubles', title: '2B', width: 28 },
      { key: 'triples', title: '3B', width: 28 },
      { key: 'home_runs', title: 'HR', width: 28 },
      { key: 'rbi', title: 'RBI', width: 32 },
      { key: 'stolen_bases', title: 'SB', width: 28 },
      { key: 'caught_stealing', title: 'CS', width: 28 },
      { key: 'walks', title: 'BB', width: 30 },
      { key: 'strikeouts', title: 'SO', width: 30 }
    ];
    
    hitterStats.forEach(stat => {
      columns.push({
        key: `stats.season.hitting.${stat.key}`,
        title: stat.title,
        width: stat.width,
        cellPadding: 'py-0 px-1',
        sortable: true,
        isStatColumn: true,
        className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
        sortValue: (player) => {
          // Sort by season stats (not rolling)
          return getSortStatsForWindow(player, dateRange)?.hitting?.[stat.key] ?? 0;
        },
        render: (player, rowType) => {
          const statsData = rowType === 'season'
            ? player.stats?.season
            : getRollingStatsForWindow(player, dateRange);
          const value = statsData?.hitting?.[stat.key] ?? 0;

          const colorClass = rowType === 'season'
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400 opacity-60';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';

          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
        }
      });
    });
    
    // HR/AB metric - CANONICAL: calculated from stats.season.hitting.at_bats, stats.season.hitting.home_runs
    columns.push({
      key: 'hr_per_ab',
      title: 'HR/AB',
      width: 42,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        // 9o.6.e Commit 8: sort by active window (aligns with server ORDER BY).
        const statsData = getSortStatsForWindow(player, dateRange);
        const ab = statsData?.hitting?.at_bats || 0;
        const hr = statsData?.hitting?.home_runs || 0;
        return ab > 0 ? (hr / ab) : 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const ab = statsData?.hitting?.at_bats || 0;
        const hr = statsData?.hitting?.home_runs || 0;
        const value = ab > 0 ? renderPercent((hr / ab) * 100) : '0.0%';

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
      }
    });
    
    // Rate stats - CANONICAL: stats.season.hitting.* / stats.rolling_14_day.hitting.*
    const rateStats = [
      { key: 'batting_avg', title: 'AVG', width: 36, format: 'avg' },
      { key: 'obp', title: 'OBP', width: 36, format: 'avg' },
      { key: 'slg', title: 'SLG', width: 36, format: 'avg' },
      { key: 'ops', title: 'OPS', width: 38, format: 'float3' }
    ];
    
    rateStats.forEach(stat => {
      columns.push({
        key: `stats.season.hitting.${stat.key}`,
        title: stat.title,
        width: stat.width,
        cellPadding: 'py-0 px-1',
        sortable: true,
        isStatColumn: true,
        className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
        sortValue: (player) => {
          // Sort by season stats (not rolling)
          // Sort comparator needs a numeric value; `|| 0` coerces null/undefined for arithmetic
          // comparison (NOT bug-masking). Window-aware per 9o.6.e Commit 8.
          return parseFloat(getSortStatsForWindow(player, dateRange)?.hitting?.[stat.key]) || 0;
        },
        render: (player, rowType) => {
          const statsData = rowType === 'season'
            ? player.stats?.season
            : getRollingStatsForWindow(player, dateRange);
          const value = statsData?.hitting?.[stat.key] ?? 0;

          const formatted = stat.format === 'avg' ? renderAvg(value) : renderFloat3(value);
          const colorClass = rowType === 'season'
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400 opacity-60';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';

          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formatted}</span>;
        }
      });
    });
    
  } else {
    // Pitcher stats - ALL CANONICAL: stats.season.pitching.* / stats.rolling_14_day.pitching.*
    const pitcherStats = [
      { key: 'games_started', title: 'GS', width: 30 },
      { key: 'wins', title: 'W', width: 25 },
      { key: 'losses', title: 'L', width: 25 },
      { key: 'saves', title: 'SV', width: 25 },
      { key: 'blown_saves', title: 'BS', width: 25 },
      { key: 'holds', title: 'HLD', width: 25 },
      { key: 'quality_starts', title: 'QS', width: 25 }
    ];
    
    pitcherStats.forEach(stat => {
      columns.push({
        key: `stats.season.pitching.${stat.key}`,
        title: stat.title,
        width: stat.width,
        cellPadding: 'py-0 px-1',
        sortable: true,
        isStatColumn: true,
        className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
        sortValue: (player) => {
          // Sort by season stats (not rolling)
          return getSortStatsForWindow(player, dateRange)?.pitching?.[stat.key] ?? 0;
        },
        render: (player, rowType) => {
          const statsData = rowType === 'season'
            ? player.stats?.season
            : getRollingStatsForWindow(player, dateRange);
          const value = statsData?.pitching?.[stat.key] ?? 0;

          const colorClass = rowType === 'season'
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400 opacity-60';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';

          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
        }
      });
    });
    
    // Innings Pitched
    columns.push({
      key: 'stats.season.pitching.innings_pitched',
      title: 'IP',
      width: 30,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        return parseFloat(getSortStatsForWindow(player, dateRange)?.pitching?.innings_pitched) || 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const value = statsData?.pitching?.innings_pitched ?? 0;

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderFloat1(value)}</span>;
      }
    });
    
    // More pitcher counting stats
    const pitcherCountStats = [
      { key: 'hits_allowed', title: 'H', width: 25 },
      { key: 'runs_allowed', title: 'R', width: 25 },
      { key: 'earned_runs', title: 'ER', width: 25 },
      { key: 'home_runs_allowed', title: 'HR', width: 25 },
      { key: 'walks_allowed', title: 'BB', width: 25 },
      { key: 'strikeouts_pitched', title: 'SO', width: 25 }
    ];
    
    pitcherCountStats.forEach(stat => {
      columns.push({
        key: `stats.season.pitching.${stat.key}`,
        title: stat.title,
        width: stat.width,
        cellPadding: 'py-0 px-1',
        sortable: true,
        isStatColumn: true,
        className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
        sortValue: (player) => {
          return getSortStatsForWindow(player, dateRange)?.pitching?.[stat.key] ?? 0;
        },
        render: (player, rowType) => {
          const statsData = rowType === 'season'
            ? player.stats?.season
            : getRollingStatsForWindow(player, dateRange);
          const value = statsData?.pitching?.[stat.key] ?? 0;

          const colorClass = rowType === 'season'
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400 opacity-60';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';

          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderDefault(value)}</span>;
        }
      });
    });
    
    // Rate stats - ERA, WHIP
    columns.push({
      key: 'stats.season.pitching.era',
      title: 'ERA',
      width: 30,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        return parseFloat(getSortStatsForWindow(player, dateRange)?.pitching?.era) || 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const value = statsData?.pitching?.era ?? 0;

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderFloat2(value)}</span>;
      }
    });

    columns.push({
      key: 'stats.season.pitching.whip',
      title: 'WHIP',
      width: 35,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        return parseFloat(getSortStatsForWindow(player, dateRange)?.pitching?.whip) || 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const value = statsData?.pitching?.whip ?? 0;

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{renderFloat3(value)}</span>;
      }
    });
    
    // K/9 and BB/9 - calculated metrics
    columns.push({
      key: 'k_per_9',
      title: 'K/9',
      width: 30,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        // 9o.6.e Commit 8: sort by active window (aligns with server ORDER BY).
        const statsData = getSortStatsForWindow(player, dateRange);
        const ip = parseFloat(statsData?.pitching?.innings_pitched) || 0;
        const k = statsData?.pitching?.strikeouts_pitched || 0;
        return ip > 0 ? (k * 9 / ip) : 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const ip = parseFloat(statsData?.pitching?.innings_pitched) || 0;
        const k = statsData?.pitching?.strikeouts_pitched || 0;
        const value = ip > 0 ? ((k * 9) / ip).toFixed(1) : '0.0';

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
      }
    });
    
    columns.push({
      key: 'bb_per_9',
      title: 'BB/9',
      width: 30,
      cellPadding: 'py-0 px-1',
      sortable: true,
      isStatColumn: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      sortValue: (player) => {
        // 9o.6.e Commit 8: sort by active window (aligns with server ORDER BY).
        const statsData = getSortStatsForWindow(player, dateRange);
        const ip = parseFloat(statsData?.pitching?.innings_pitched) || 0;
        const bb = statsData?.pitching?.walks_allowed || 0;
        return ip > 0 ? (bb * 9 / ip) : 0;
      },
      render: (player, rowType) => {
        const statsData = rowType === 'season'
          ? player.stats?.season
          : getRollingStatsForWindow(player, dateRange);
        const ip = parseFloat(statsData?.pitching?.innings_pitched) || 0;
        const bb = statsData?.pitching?.walks_allowed || 0;
        const value = ip > 0 ? ((bb * 9) / ip).toFixed(1) : '0.0';

        const colorClass = rowType === 'season'
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400 opacity-60';
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';

        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
      }
    });
  }

  // Contract Length column (only in All Players mode) - Show on rolling row only
  if (showAll) {
    columns.push({
      key: 'league_context.financial.contract_years',
      title: 'Contract',
      width: 40,
      cellPadding: 'py-0 px-1',
      sortable: true,
      className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
      render: (player, rowType) => {
        if (rowType !== 'rolling') return null;
        
        const teamName = player.league_context?.team?.team_name;
        if (teamName) {
          const contract = getContract(player);
          const display = formatContractDisplay(contract?.contract_type, contract?.contract_years);
          return (
            <div className="flex items-center justify-center h-full">
              <span className={dynastyTheme.classes.text.neutralLight}>
                {display}
              </span>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-full">
            <span className={dynastyTheme.classes.text.neutral}>-</span>
          </div>
        );
      }
    });
  }

  // Price column - CANONICAL: league_context.financial.baseline_price
  // ALWAYS reads from league_context.financial (player_prices table), NOT projected_contract
  // projected_contract has salary/contract info but NOT baseline_price or current_market_value
  columns.push({
    key: 'price',
    title: 'Price',
    width: 44,
    cellPadding: 'py-0 px-1',
    sortable: true,
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    sortValue: (player) => {
      const financial = player.league_context?.financial;
      const contract = getContract(player);
      const ct = contract?.contract_type || '';
      if (ct.startsWith('OP') && (contract?.salary || contract?.contract_salary) > 0) {
        return contract.salary || contract.contract_salary;
      }
      return financial?.baseline_price ?? 0;
    },
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const financial = player.league_context?.financial;
      const contract = getContract(player);
      const contractType = contract?.contract_type || '';
      const hasRetainedContract = contractType.startsWith('OP') && (contract?.salary || contract?.contract_salary) > 0;
      const amount = hasRetainedContract
        ? (contract.salary || contract.contract_salary)
        : financial?.baseline_price;

      if (amount === null || amount === undefined) {
        return (
          <div className="flex items-center justify-center h-full">
            <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
              -
            </span>
          </div>
        );
      }

      const mv = financial?.current_market_value;
      let priceColor = '#eab308'; // yellow = fair
      const roundedPrice = Math.round(amount);
      const roundedMV = Math.round(mv);
      if (roundedPrice < roundedMV) priceColor = '#4ade80';  // green = below market = discount
      if (roundedPrice > roundedMV) priceColor = '#f87171';  // red = above market = expensive

      return (
        <div className="flex items-center justify-center h-full">
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: priceColor }}>
            {amount > 0 ? `$${Math.round(amount)}` : '-'}
          </span>
        </div>
      );
    }
  });

  // MV Column (Market Value) - ALWAYS reads from league_context.financial
  columns.push({
    key: 'market_value',
    title: 'MV',
    width: 38,
    cellPadding: 'py-0 px-1',
    sortable: true,
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    sortValue: (player) => {
      const financial = player.league_context?.financial;
      return financial?.current_market_value > 0
        ? financial.current_market_value
        : financial?.baseline_price ?? 0;
    },
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const financial = player.league_context?.financial;
      const mv = financial?.current_market_value;
      const baseline = financial?.baseline_price;

      if ((mv === null || mv === undefined || mv <= 0) && (!baseline || baseline <= 0)) {
        return (
          <div className="flex items-center justify-center h-full">
            <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>-</span>
          </div>
        );
      }

      const displayValue = mv > 0 ? mv : baseline;

      return (
        <div className="flex items-center justify-center h-full">
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: displayValue > 0 ? '#9ca3af' : '#6b7280' }}>
            {displayValue > 0 ? `$${Math.round(displayValue)}` : 'N/A'}
          </span>
        </div>
      );
    }
  });

  // Action column - Show on rolling row only
  columns.push({
    key: 'action',
    title: 'Action',
    width: 50,
    cellPadding: 'py-0 px-1',
    sortable: false,
    className: (player, rowType) => rowType === 'rolling' ? 'border-b border-yellow-500/20' : '',
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      return (
        <ActionButton
          player={player}
          handleAddPlayer={handleAddPlayer}
          transactionsEnabled={transactionsEnabled}
          addingPlayer={addingPlayer}
          loading={loading}
          isCommissionerMode={isCommissionerMode}
          activeTeamName={activeTeamName}
          myTeamId={activeTeamId}
          leagueStatus={leagueStatus}
          leagueId={leagueId}
          currentRoster={currentRoster}
        />
      );
    }
  });

  return columns;
};
